import axios from "axios";
import router from '@/router';
import { getTokenSilently } from "@/services/AuthService";

const BASE_URL = process.env.VUE_APP_API_URL;
const API_AUDIENCE = process.env.VUE_APP_AUTH0_AUDIENCE;

if (!BASE_URL) throw new Error("VUE_APP_API_URL is not configured.");
if (!API_AUDIENCE) throw new Error("VUE_APP_AUTH0_AUDIENCE is not configured.");

let unauthorizedHandler = null;

export function setUnauthorizedHandler(fn) {
    unauthorizedHandler = fn;
}

export class ApiError extends Error {
    constructor(status, message, errors = null, traceId = null) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.errors = errors;   // ValidationProblemDetails.errors (field -> [messages])
        this.traceId = traceId; // ProblemDetails traceId for support correlation
    }
}

class ClientBase {
    constructor() {
        this.api = axios.create({ baseURL: BASE_URL });
        // Tracks GETs currently in flight, keyed by endpoint+params, so identical concurrent
        // requests (e.g. several dashboard cards all loading the same endpoint on mount) share a
        // single network call instead of each firing their own.
        this._inFlightGets = new Map();
        this._setupRequestInterceptor();
        this._setupResponseInterceptor();
    }
    

    _setupRequestInterceptor() {
        this.api.interceptors.request.use(async (config) => {
            const token = await getTokenSilently({ audience: API_AUDIENCE });
            config.headers.Authorization = `Bearer ${token}`;
            return config;
        });
    }

    _setupResponseInterceptor() {
        this.api.interceptors.response.use(
            (response) => {
                if (response.status === 204)
                    return null;

                const body = response.data;
                return body?.Data ?? body?.data ?? body;
            },
            (error) => {
                if (!error.response) {
                    if (process.env.NODE_ENV === 'production') {
                        router.push({ name: 'ServiceUnavailable' });
                    }
                    throw new ApiError(0, "No response received from server.");
                }

                const { status, data } = error.response;
                // API errors are RFC 7807 ProblemDetails: { title, detail, status, traceId, errors? }
                const message = data?.detail ?? data?.title ?? data?.message ?? data?.Message ?? "An unexpected error occurred.";
                const errors = data?.errors ?? data?.Errors ?? null;
                const traceId = data?.traceId ?? null;

                if (status === 401) {
                    unauthorizedHandler?.();
                    throw new ApiError(status, "Unauthorized. Please log in.");
                }

                if (status === 403) {
                    router.push({ name: 'ForbiddenPage' });
                    throw new ApiError(status, "You do not have permission to perform this action.");
                }

                if (status >= 500) {
                    if (process.env.NODE_ENV === 'production') {
                        router.push({ name: 'ServiceUnavailable' });
                    }
                    throw new ApiError(status, message, errors, traceId);
                }

                throw new ApiError(status, message, errors, traceId);
            }
        );
    }

    async get(endpoint, params = {}) {
        // De-duplicate identical concurrent GETs: if the same request is already pending, return
        // its promise instead of issuing a second network call. The entry is removed once the
        // request settles, so subsequent loads still hit the network for fresh data — this is
        // concurrency de-duplication, NOT a time-based cache, so there's no staleness risk.
        const key = `${endpoint}|${JSON.stringify(params ?? {})}`;
        const existing = this._inFlightGets.get(key);
        if (existing) return existing;

        const request = this.api.get(endpoint, { params })
            .finally(() => this._inFlightGets.delete(key));

        this._inFlightGets.set(key, request);
        return request;
    }

    /**
     * Fetches every page of a paginated list endpoint and returns the flattened items.
     * The API returns { items, continuationToken }; callers expecting a full array stay unchanged.
     */
    async getAllPaged(endpoint, params = {}, pageSize = 200) {
        const all = [];
        let continuationToken = null;
        do {
            const query = { ...params, pageSize };
            if (continuationToken) query.continuationToken = continuationToken;
            const page = await this.get(endpoint, query);
            const items = Array.isArray(page) ? page : (page?.items ?? []);
            all.push(...items);
            continuationToken = (page && !Array.isArray(page)) ? (page.continuationToken ?? null) : null;
        } while (continuationToken);
        return all;
    }

    async post(endpoint, data) {
        return await this.api.post(endpoint, data);
    }

    async put(endpoint, data, config = {}) {
        return await this.api.put(endpoint, data, config);
    }

    async delete(endpoint, params = {}) {
        return await this.api.delete(endpoint, { params });
    }
}

export default ClientBase;